import React, { useState, useEffect } from 'react';
import conceptosAfectanLicenciaGoceHaberService from '../services/conceptosAfectanLicenciaGoceHaberService';

const ConceptosAfectanLicenciaGoceHaberModal = ({ isOpen, onClose, empresaId, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [conceptosIngresos, setConceptosIngresos] = useState([]);
  const [conceptosSeleccionados, setConceptosSeleccionados] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && empresaId) {
      cargarDatos();
    }
  }, [isOpen, empresaId]);

  const cargarDatos = async () => {
    setLoading(true);
    setError('');

    try {
      const [ingresosResponse, configuradosResponse] = await Promise.all([
        conceptosAfectanLicenciaGoceHaberService.obtenerConceptosIngresosDisponibles(),
        conceptosAfectanLicenciaGoceHaberService.obtenerConceptos(empresaId),
      ]);

      if (ingresosResponse.success) {
        const ingresosFiltered = ingresosResponse.data.filter(
          (concepto) => concepto.tipo_concepto === 'ingreso'
        );
        setConceptosIngresos(ingresosFiltered);
      } else {
        setError('Error al cargar conceptos de ingresos');
      }

      if (configuradosResponse.success) {
        const ids = new Set(configuradosResponse.data.map((c) => c.id_concepto_plame));
        setConceptosSeleccionados(ids);
      }
    } catch (err) {
      console.error('Error al cargar datos:', err);
      setError('Error al cargar la información');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleConcepto = (conceptoId) => {
    const nuevos = new Set(conceptosSeleccionados);
    if (nuevos.has(conceptoId)) nuevos.delete(conceptoId);
    else nuevos.add(conceptoId);
    setConceptosSeleccionados(nuevos);
  };

  const getConceptosFiltrados = () => {
    if (!searchTerm) return conceptosIngresos;
    const q = searchTerm.toLowerCase();
    return conceptosIngresos.filter(
      (concepto) =>
        concepto.nombre.toLowerCase().includes(q) || concepto.codigo.toLowerCase().includes(q)
    );
  };

  const conceptosFiltrados = getConceptosFiltrados();

  const handleSelectAll = () => {
    const todosSel = conceptosFiltrados.every((c) => conceptosSeleccionados.has(c.id));
    const nuevos = new Set(conceptosSeleccionados);
    if (todosSel) conceptosFiltrados.forEach((c) => nuevos.delete(c.id));
    else conceptosFiltrados.forEach((c) => nuevos.add(c.id));
    setConceptosSeleccionados(nuevos);
  };

  const handleGuardar = async () => {
    setSaving(true);
    setError('');

    try {
      const conceptosIds = Array.from(conceptosSeleccionados);
      const response = await conceptosAfectanLicenciaGoceHaberService.configurar(empresaId, conceptosIds);

      if (response.success) {
        onSuccess && onSuccess('Conceptos que afectan licencia con goce de haber guardados correctamente');
        onClose();
      } else {
        let msg = response.error || 'Error al guardar la configuración';
        const errs = response.errors;
        if (errs && typeof errs === 'object') {
          const lines = Object.entries(errs).flatMap(([field, messages]) => {
            const arr = Array.isArray(messages) ? messages : [messages];
            return arr.map((m) => `${field}: ${m}`);
          });
          if (lines.length) msg = lines.join(' · ');
        }
        setError(msg);
      }
    } catch (err) {
      console.error('Error al guardar:', err);
      setError('Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  const todosSeleccionados =
    conceptosFiltrados.length > 0 && conceptosFiltrados.every((c) => conceptosSeleccionados.has(c.id));

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-teal-50 to-indigo-50 p-6 border-b border-teal-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900 flex items-center">
                <span className="text-2xl mr-3">🧾</span>
                Conceptos que afectan licencia con goce de haber
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Selecciona los conceptos PLAME de ingresos cuyo proporcional debe descontar los días registrados
                como licencia con goce de haber en tareos (p. ej. remuneración básica).
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden flex flex-col">
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <svg
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  <input
                    type="text"
                    placeholder="Buscar por nombre o código..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={handleSelectAll}
                className="px-4 py-2 text-sm font-medium text-teal-700 bg-teal-100 rounded-lg hover:bg-teal-200 transition-colors"
              >
                {todosSeleccionados ? 'Deseleccionar todo (vista)' : 'Seleccionar todo (vista)'}
              </button>
            </div>
            <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
              <span>{conceptosFiltrados.length} conceptos disponibles</span>
              <span>{conceptosSeleccionados.size} seleccionados</span>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500 mx-auto" />
                  <p className="mt-2 text-gray-600">Cargando conceptos...</p>
                </div>
              </div>
            ) : error && !conceptosFiltrados.length ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700">{error}</p>
              </div>
            ) : conceptosFiltrados.length === 0 ? (
              <div className="text-center py-12 text-gray-500">No se encontraron conceptos</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {conceptosFiltrados.map((concepto) => {
                  const sel = conceptosSeleccionados.has(concepto.id);
                  return (
                    <div
                      key={concepto.id}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === 'Enter' && handleToggleConcepto(concepto.id)}
                      className={`group relative border rounded-xl p-5 cursor-pointer transition-all duration-300 hover:scale-[1.01] ${
                        sel
                          ? 'border-teal-500 bg-gradient-to-br from-teal-50 to-indigo-50 shadow-lg ring-2 ring-teal-200'
                          : 'border-gray-200 bg-white hover:border-teal-300 hover:shadow-md'
                      }`}
                      onClick={() => handleToggleConcepto(concepto.id)}
                    >
                      <div className="absolute top-4 right-4">
                        <div
                          className={`relative w-5 h-5 rounded border-2 ${
                            sel ? 'border-teal-500 bg-teal-500' : 'border-gray-300 group-hover:border-teal-400'
                          }`}
                        >
                          {sel && (
                            <svg
                              className="w-3 h-3 text-white absolute top-0.5 left-0.5"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                          )}
                        </div>
                      </div>
                      <div className="flex items-start space-x-4 pr-6">
                        <div
                          className={`flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center ${
                            sel ? 'bg-teal-600 text-white' : 'bg-teal-100 text-teal-700'
                          }`}
                        >
                          <span className="text-lg">🧾</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className={`text-sm font-semibold mb-2 ${sel ? 'text-teal-900' : 'text-gray-900'}`}>
                            {concepto.nombre}
                          </h4>
                          <span
                            className={`inline-flex px-2.5 py-1 rounded-md text-xs font-medium ${
                              sel ? 'bg-teal-200 text-teal-800' : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {concepto.codigo}
                          </span>
                          {concepto.clasificacion && (
                            <p className={`text-xs mt-2 ${sel ? 'text-teal-700' : 'text-gray-500'}`}>
                              {concepto.clasificacion}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="absolute bottom-4 right-4">
                        <span
                          className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                            sel ? 'bg-indigo-600 text-white' : 'bg-indigo-100 text-indigo-700'
                          }`}
                        >
                          {sel ? 'Afecta lic. goce' : 'Ingreso'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {error && conceptosFiltrados.length > 0 && (
              <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{error}</div>
            )}
          </div>
        </div>

        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex flex-col sm:flex-row justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleGuardar}
              disabled={saving || loading}
              className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  Guardando...
                </>
              ) : (
                'Guardar configuración'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConceptosAfectanLicenciaGoceHaberModal;
